import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { novoRegistroBase, type Meta } from '../../db/tipos'
import { gravarLocalEEnfileirar, apagarLocalEEnfileirar } from '../../sync/outbox'
import { dataISOLocal } from '../../pedidos/precos'
import { ROTULOS_TIPO_META, formatarValorDeMeta } from '../../metas/rotulos'
import { mesDe } from '../../metas/destrave'
import { realizadoDaMeta } from '../../metas/metasDados'
import { progressoDaMeta } from '../../metas/progresso'
// A MESMA funcao que a tela de Comissoes usa para escrever o mes. Duas telas do
// mesmo dado tem de dizer a mesma coisa -- aqui saia "2026-08" logo abaixo de um
// campo que ja mostrava "agosto de 2026".
import { rotuloMes } from '../../comissoes/conferencia'
import FormMetaNova, { type MetaNova } from './FormMetaNova'

function mesAtual(): string {
  const hoje = new Date()
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`
}

function mesVizinho(mes: string, passo: number): string {
  const [ano, m] = mes.split('-').map(Number)
  const data = new Date(ano, m - 1 + passo, 1)
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`
}

// Decisao dela em 07/10: os exemplos so usam tipos que JA existem, e tocar
// num deles abre o "Criar meta" com o tipo escolhido.
const EXEMPLOS: { tipo: Meta['tipo']; titulo: string; exemplo: string; cor: string; icone: string }[] = [
  {
    tipo: 'faturamento_total', titulo: 'Faturamento', exemplo: 'Ex.: R$ 50.000,00 de vendas',
    cor: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    icone: 'M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3zM4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6',
  },
  {
    tipo: 'positivacao', titulo: 'Clientes positivados', exemplo: 'Ex.: 40 clientes no mês',
    cor: 'bg-sky-50 border-sky-100 text-sky-700',
    icone: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1M16 3.5a4 4 0 0 1 0 7M18 14a6 6 0 0 1 4 6v1',
  },
  {
    tipo: 'itens_por_pedido', titulo: 'Itens por pedido', exemplo: 'Ex.: 8 itens por pedido',
    cor: 'bg-amber-50 border-amber-100 text-amber-700',
    icone: 'M3 4h2l2.4 11h11.2L21 7H6.2M9 20h.01M18 20h.01',
  },
]

function Icone({ d, className = 'h-6 w-6' }: { d: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.8}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  )
}

function Alvo() {
  return (
    <svg viewBox="0 0 200 120" className="h-32 w-auto mx-auto" aria-hidden="true">
      <ellipse cx="100" cy="88" rx="80" ry="28" fill="#e6f2ef" />
      <circle cx="100" cy="64" r="44" fill="#0b4a47" />
      <circle cx="100" cy="64" r="33" fill="white" />
      <circle cx="100" cy="64" r="23" fill="#0b4a47" />
      <circle cx="100" cy="64" r="13" fill="white" />
      <circle cx="100" cy="64" r="6" fill="#0b4a47" />
      <path d="M100 64 140 24" stroke="#c9a227" strokeWidth="4" strokeLinecap="round" />
      <path d="M140 24l-2-12 10 2zM140 24l12 2-2 10z" fill="#c9a227" />
      <path d="M40 46l8 4M38 62h9M42 78l7-5M160 46l-8 4M162 62h-9M158 78l-7-5" stroke="#c9a227"
        strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

export default function Metas() {
  const [mesVisto, setMesVisto] = useState(mesAtual())
  const [criandoManual, setCriandoManual] = useState(false)
  const [tipoInicial, setTipoInicial] = useState<Meta['tipo']>('valor_vendido')
  const [criada, setCriada] = useState<string | null>(null)
  const formRef = useRef<HTMLDivElement>(null)

  function abrirCriar(tipo: Meta['tipo']) {
    setCriada(null)
    setTipoInicial(tipo)
    setCriandoManual(true)
    // Os exemplos ficam no FIM da tela e o formulario la em cima: sem rolar,
    // o toque parece nao fazer nada.
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0)
  }

  const representadas = useLiveQuery(
    () => banco.representadas.filter((r) => r.apagado_em === null).sortBy('nome'),
    [],
  )

  // As metas E o realizado de cada uma na MESMA consulta, de proposito.
  //
  // ⚠ O realizado e lido AQUI DENTRO, e nao num useEffect: realizadoDaMeta
  // varre pedidos, sub_pedidos e itens_pedido, e o Dexie observa tudo que for
  // lido dentro do callback. Com leitura unica, num aparelho recem-aberto a
  // conta sairia MENOR e nunca se corrigiria -- a sincronizacao traz pedidos
  // antes dos itens. Quatro telas deste ciclo ja caíram nisso.
  const dados = useLiveQuery(async () => {
    const todas = await banco.metas.filter((m) => m.apagado_em === null).toArray()
    const doMes = todas
      .filter((m) => mesDe(m.periodo_inicio) === mesVisto)
      .sort((a, b) => a.escopo.localeCompare(b.escopo) || a.tipo.localeCompare(b.tipo))
    const realizado = new Map<string, number>()
    for (const meta of doMes) realizado.set(meta.id, await realizadoDaMeta(meta, new Date()))
    return { doMes, realizado }
  }, [mesVisto])

  const nomeEscopo = (valorEscopo: string) => {
    if (valorEscopo === 'pessoal') return 'Pessoal'
    const representada = representadas?.find(
      (r) => r.id === valorEscopo.slice('representada:'.length),
    )
    return representada?.nome ?? 'Fornecedora'
  }

  // O periodo vem do MES VISTO, e nao de campos de data: decisao dela em
  // 11/08. O ciclo inteiro e mensal (filtro, criador, repetir), e ter uma porta
  // com regra diferente das outras duas era a unica fonte de confusao.
  async function criar(nova: MetaNova) {
    const [ano, mes] = mesVisto.split('-').map(Number)
    await gravarLocalEEnfileirar('metas', {
      ...novoRegistroBase(),
      escopo: nova.escopo,
      tipo: nova.tipo,
      periodo_inicio: dataISOLocal(new Date(ano, mes - 1, 1)),
      periodo_fim: dataISOLocal(new Date(ano, mes, 0)),
      valor_alvo: nova.valorAlvo,
    })
    // Decisao dela em 08/10: o formulario NAO fecha. O mesmo exemplo costuma
    // valer para varias empresas -- troca so a empresa e o valor e cria de novo.
    setCriada(nomeEscopo(nova.escopo))
  }

  return (
    <section className="p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Metas</h2>
          <p className="text-sm text-slate-500">
            Defina suas metas mensais por empresa e acompanhe sua evolução.
          </p>
        </div>
        <Link
          to="/configuracao/metas"
          className="shrink-0 flex items-center gap-1.5 bg-teal-50 border border-teal-100 text-teal-700 rounded-full px-3 py-1.5 text-sm font-medium"
        >
          <Icone className="h-4 w-4" d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
          Configurar
        </Link>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="month"
          data-testid="filtro-mes"
          aria-label="Mês"
          value={mesVisto}
          onChange={(e) => setMesVisto(e.target.value)}
          className="flex-1 min-w-0 bg-white border border-slate-300 rounded-xl px-3 py-2.5"
        />
        <button
          type="button" aria-label="Mês anterior" onClick={() => setMesVisto((m) => mesVizinho(m, -1))}
          className="shrink-0 h-11 w-11 rounded-full border border-slate-200 bg-white text-slate-600 flex items-center justify-center"
        >
          <Icone className="h-5 w-5" d="M19 12H5M11 6l-6 6 6 6" />
        </button>
        <button
          type="button" aria-label="Próximo mês" onClick={() => setMesVisto((m) => mesVizinho(m, 1))}
          className="shrink-0 h-11 w-11 rounded-full border border-slate-200 bg-white text-slate-600 flex items-center justify-center"
        >
          <Icone className="h-5 w-5" d="M5 12h14M13 6l6 6-6 6" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <button
          data-testid="porta-criar"
          onClick={() => (criandoManual ? setCriandoManual(false) : abrirCriar('valor_vendido'))}
          className="bg-teal-700 text-white rounded-2xl p-3 flex flex-col items-center gap-1 text-center"
        >
          <span className="h-8 w-8 rounded-full bg-white text-teal-700 flex items-center justify-center">
            <Icone className="h-5 w-5" d="M12 5v14M5 12h14" />
          </span>
          <span className="font-bold text-sm">Criar meta</span>
          <span className="text-xs text-teal-50">Adicionar nova meta</span>
        </button>
        <Link
          data-testid="porta-criador"
          to="/metas/criador"
          className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-col items-center gap-1 text-center"
        >
          <span className="h-8 flex items-center text-teal-700">
            <Icone d="M15 4V2M15 10V8M11 6h2M17 6h2M4 20 14 10M18 13v2M17 14h2M6 3v2M5 4h2" />
          </span>
          <span className="font-bold text-sm text-teal-800">Criador</span>
          <span className="text-xs text-slate-500">Sugerir com base no seu histórico</span>
        </Link>
        <Link
          data-testid="porta-repetir"
          to="/metas/repetir"
          className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-col items-center gap-1 text-center"
        >
          <span className="h-8 flex items-center text-teal-700">
            <Icone d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />
          </span>
          <span className="font-bold text-sm text-teal-800">Repetir</span>
          <span className="text-xs text-slate-500">Trazer metas do mês anterior</span>
        </Link>
      </div>

      {criandoManual && (
        <div ref={formRef} className="bg-white rounded-xl shadow-sm p-4 space-y-2">
          <p className="text-xs text-slate-500">
            A meta vale o mês {mesVisto}, escolhido acima.
          </p>
          {/* key: trocar de exemplo com o formulario aberto tem de trocar o tipo. */}
          <FormMetaNova
            key={tipoInicial} tipoInicial={tipoInicial}
            rotuloBotao="Criar meta" aoIncluir={(nova) => void criar(nova)}
          />
          {criada && (
            <p data-testid="meta-criada" className="text-sm text-teal-700">
              ✓ Meta criada ({criada}). Para outra empresa, troque a empresa e o valor.
            </p>
          )}
        </div>
      )}

      {/* undefined = ainda carregando; lista vazia = mes sem metas. Sem esta
          distincao o mes vazio piscaria "Carregando..." para sempre. */}
      {dados === undefined && <p className="text-sm text-slate-500">Carregando…</p>}

      {/* Decisao dela em 07/10: o quadro do alvo so aparece no mes vazio. */}
      {dados !== undefined && dados.doMes.length === 0 && (
        <>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 text-center space-y-3">
            <Alvo />
            <h3 className="text-lg font-bold text-slate-900">Nenhuma meta em {rotuloMes(mesVisto)}</h3>
            <p className="text-sm text-slate-500">
              Defina suas metas para acompanhar seu desempenho no mês. Você pode criar uma meta
              avulsa, usar o Criador para sugestões ou repetir as metas do mês anterior.
            </p>
            <Link
              to="/metas/como-funciona"
              className="inline-flex items-center gap-2 border border-teal-200 text-teal-700 rounded-full px-4 py-2 text-sm font-medium"
            >
              <Icone className="h-5 w-5" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM10 8.5v7l5.5-3.5z" />
              Ver como funciona
            </Link>
          </div>
        </>
      )}

      <ul className="space-y-2">
        {dados?.doMes.map((meta) => {
          const feito = dados.realizado.get(meta.id) ?? 0
          const { pct, atingida } = progressoDaMeta(feito, meta.valor_alvo)
          return (
            <li
              key={meta.id}
              data-testid={`meta-${meta.id}`}
              className="bg-white rounded-xl shadow-sm p-3 space-y-2"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-medium text-slate-800">
                  {ROTULOS_TIPO_META[meta.tipo]} — {nomeEscopo(meta.escopo)}
                </span>
                <button
                  onClick={() => void apagarLocalEEnfileirar('metas', meta.id)}
                  className="text-red-600 text-xs underline shrink-0"
                >
                  remover
                </button>
              </div>

              <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                <div
                  data-testid={`barra-${meta.id}`}
                  className={`h-full ${atingida ? 'bg-emerald-500' : 'bg-teal-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>

              {/* O realizado sai SEM teto: a barra para em 100%, o numero nao.
                  Vender 150% da meta e informacao que ela precisa ver. */}
              <span className="block text-xs text-slate-600">
                {formatarValorDeMeta(meta.tipo, feito)} de{' '}
                {formatarValorDeMeta(meta.tipo, meta.valor_alvo)}
                {atingida && <strong className="text-emerald-700"> — meta batida ✓</strong>}
              </span>
            </li>
          )
        })}
      </ul>

      {/* Os exemplos ficam SEMPRE (ela, 07/10): o mesmo exemplo pode servir
          para cada empresa, uma de cada vez. */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">Exemplos de metas</h3>
          <button
            type="button" onClick={() => abrirCriar(EXEMPLOS[0].tipo)}
            className="text-sm text-teal-700 font-medium flex items-center gap-0.5"
          >
            Usar modelo <Icone className="h-4 w-4" d="M9 6l6 6-6 6" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {EXEMPLOS.map((ex) => (
            <button
              key={ex.tipo} type="button" data-testid={`exemplo-${ex.tipo}`}
              onClick={() => abrirCriar(ex.tipo)}
              className={`border rounded-2xl p-3 text-left space-y-1 ${ex.cor}`}
            >
              <Icone d={ex.icone} />
              <span className="block font-bold text-sm text-slate-900">{ex.titulo}</span>
              <span className="block text-xs text-slate-500">{ex.exemplo}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
